<template>
  <v-container fluid class="wm-wrap pa-0">
    <!-- Permission denied. The router guard already blocks this route; this covers
         the case where the cached permission changes after entry. -->
    <v-alert v-if="!weldMappingEnabled" type="warning" text prominent class="ma-4">
      {{ $t('message.weldMapping.featureDisabled') }}
    </v-alert>

    <template v-else>
      <!-- ── header ───────────────────────────────────────────── -->
      <div class="wm-top">
        <v-btn icon class="wm-ib" :aria-label="$t('message.login.back')" @click="goBack">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <div class="wm-grow">
          <h1 class="wm-h1">{{ $t('message.weldMapping.title') }}</h1>
          <div class="wm-sub">{{ projectLine }}</div>
        </div>
        <!-- Connection status reuses the PWA's existing offline flag. -->
        <span class="wm-pill" :class="isOnline ? 'ok' : 'off'">
          <span class="wm-dot" :class="isOnline ? 'g' : 'o'"></span>
          {{ isOnline ? $t('message.common.online') : $t('message.common.offline') }}
        </span>
      </div>

      <!-- ── search (drawings only) ────────────────────────────── -->
      <div class="wm-pad">
        <v-text-field
          v-model="search"
          :placeholder="$t('message.weldMapping.searchDrawing')"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          class="wm-search"
        ></v-text-field>
      </div>

      <!-- ── breadcrumb: current level only, no path compression ── -->
      <div v-if="stack.length" class="wm-crumbs wm-pad">
        <button class="wm-crumb" @click="resetLevel">{{ $t('message.weldMapping.allDrawings') }}</button>
        <template v-for="(node, i) in stack">
          <span :key="'s' + i" class="wm-crumb-sep">›</span>
          <button
            :key="'c' + i"
            class="wm-crumb"
            :class="{ here: i === stack.length - 1 }"
            @click="gotoLevel(i)"
          >{{ nodeNumber(node) }}</button>
        </template>
      </div>

      <!-- ── loading ──────────────────────────────────────────── -->
      <div v-if="loading" class="wm-pad">
        <v-row>
          <v-col v-for="n in 6" :key="n" cols="12" sm="6" md="4" class="pa-2">
            <v-skeleton-loader type="image, list-item-two-line"></v-skeleton-loader>
          </v-col>
        </v-row>
      </div>

      <!-- ── API error, never a blank page ────────────────────── -->
      <v-alert v-else-if="loadError" type="error" text prominent class="ma-4">
        {{ loadError }}
        <div class="mt-2">
          <v-btn small outlined @click="loadDrawings">{{ $t('message.common.retry') }}</v-btn>
        </div>
      </v-alert>

      <template v-else>
        <!-- ── continue: only when a real drawing was opened before ── -->
        <template v-if="continueCards.length">
          <div class="wm-sec"><h2>{{ $t('message.weldMapping.continue') }}</h2></div>
          <div class="wm-pad">
            <button
              v-for="c in continueCards" :key="c.id"
              class="wm-wide" @click="openDrawing(c)"
            >
              <div class="wm-wide-th">
                <weld-map-canvas
                  v-if="hasPreview(c.id)"
                  preview
                  :joints="previews[c.id].joints"
                  :drawing-file="previews[c.id].drawing_file"
                  :label="c.number"
                  :drawing-id="c.id"
                />
                <v-icon v-else size="30" class="wm-ph">mdi-file-document-outline</v-icon>
              </div>
              <div class="wm-wide-bd">
                <div class="wm-no">{{ c.number }}</div>
                <div v-if="c.description" class="wm-meta">{{ c.description }}</div>
                <!-- TODO(revision): the Rev chip belongs here. Deliberately not
                     rendered — project_drawing has no revision column, and `tag`
                     must not be used as a stand-in. Needs a drawing_revision
                     table (drawing_id, rev, issued_at, superseded_at, file_key)
                     with welds carrying drawing_revision_id, so a weld stays
                     linked to the exact revision it was made against. -->
                <div v-if="c.openedAt" class="wm-meta wm-accent">
                  {{ $t('message.weldMapping.lastOpened') }}: {{ c.openedAt | fromNow }}
                </div>
              </div>
              <v-icon class="wm-ph">mdi-chevron-right</v-icon>
            </button>
          </div>
        </template>

        <!-- A drawing can have welds of its own AND sub-drawings. Drilling in
             would otherwise strand the parent's own welds with no card to open
             them, so they get an explicit entry rather than an implicit
             roll-up into the children. -->
        <template v-if="currentOwnWelds > 0">
          <div class="wm-sec"><h2>{{ $t('message.weldMapping.ownJoints') }}</h2></div>
          <div class="wm-pad">
            <button class="wm-wide" @click="openCurrentOwn">
              <div class="wm-wide-th">
                <v-icon size="30" class="wm-ph">mdi-map-marker-outline</v-icon>
              </div>
              <div class="wm-wide-bd">
                <div class="wm-no">{{ $t('message.weldMapping.ownMap', { number: currentNumber }) }}</div>
                <div class="wm-meta">{{ $t('message.weldMapping.ownSub', { count: currentOwnWelds }) }}</div>
              </div>
              <v-icon class="wm-ph">mdi-chevron-right</v-icon>
            </button>
          </div>
        </template>

        <!-- ── all drawings ─────────────────────────────────────── -->
        <div class="wm-sec">
          <h2>{{ stack.length ? $t('message.weldMapping.childDrawings') : $t('message.weldMapping.allDrawings') }}</h2>
          <span v-if="visible.length" class="wm-n">{{ visible.length }}</span>
        </div>

        <!-- empty state -->
        <div v-if="!visible.length" class="wm-empty">
          <b>{{ search ? $t('message.weldMapping.noMatch') : $t('message.advancedWeldLog.noDrawingsFound') }}</b>
          <span v-if="search">{{ $t('message.weldMapping.noMatchHint') }}</span>
        </div>

        <!-- 3 cols on wide tablets, 2 on narrow tablets, 1 on phones -->
        <v-row v-else class="wm-pad ma-0">
          <v-col
            v-for="d in rendered"
            :key="d.id"
            cols="12"
            sm="6"
            md="4"
            class="pa-2"
          >
            <button
              class="wm-card"
              :class="{ 'wm-card-sel': selectedId === d.id && !d.hasChildren }"
              @click="onCardClick(d)"
            >
              <!-- The same map the drawing screen renders, as a static preview:
                   one renderer, so what the card promises is what opens. Falls
                   back to the placeholder icon when the drawing has no image. -->
              <div class="wm-th">
                <weld-map-canvas
                  v-if="hasPreview(d.id)"
                  preview
                  :joints="previews[d.id].joints"
                  :drawing-file="previews[d.id].drawing_file"
                  :label="d.number"
                  :drawing-id="d.id"
                />
                <v-icon v-else size="34" :color="d.hasChildren ? '#8A9099' : '#1D6FD6'">
                  {{ d.hasChildren ? 'mdi-folder-multiple-outline' : 'mdi-file-document-outline' }}
                </v-icon>
              </div>
              <div class="wm-bd">
                <div class="wm-no">{{ d.number }}</div>
                <div v-if="d.description" class="wm-meta">{{ d.description }}</div>
                <!-- TODO(revision): Rev chip goes here once a drawing_revision
                     table exists. Never infer it from `tag`. -->

                <div class="wm-meta wm-tags">
                  <span v-if="d.statusLabel" class="wm-tag" :class="statusClass(d.statusValue, d.stage)">
                    {{ d.statusLabel }}
                  </span>
                  <span v-if="d.hasChildren" class="wm-tag wm-tag-parent">
                    {{ $t('message.weldMapping.hasChildren', { count: d.childCount }) }}
                  </span>
                </div>

                <div class="wm-foot">
                  <span v-if="d.weldCount !== null">
                    <b>{{ d.weldCount }}</b> {{ $t('message.weldMapping.welds') }}
                  </span>
                  <span v-else></span>
                  <!-- Both lead somewhere now: a parent into its child list, a
                       leaf into the drawing screen. -->
                  <v-icon size="18" class="wm-chev">mdi-chevron-right</v-icon>
                </div>
              </div>
            </button>
          </v-col>
        </v-row>

        <!-- Lazy loading: cards are rendered a page at a time and this sentinel
             pulls in the next page as it scrolls into view. Keeps the card count
             — and with it the number of preview requests and canvases — bounded
             on projects with many drawings. -->
        <div v-if="hasMore" ref="sentinel" class="wm-more">
          <v-progress-circular indeterminate size="22" width="2" color="#1D6FD6" />
        </div>
        <div style="height:24px"></div>
      </template>
    </template>
  </v-container>
</template>

<script>
/* eslint-disable */
import { mapGetters } from 'vuex'
import { hostAppApi } from '@/plugins/axios_settings'
import WeldMappingMixin from '@/mixins/weldMapping'
// The drawing screen's own canvas, reused as a static card thumbnail so the
// preview and the opened drawing are rendered by one and the same code.
import WeldMapCanvas from '@/components/WeldMapping/WeldMapCanvas'
import moment from 'moment'
// Styles live in their own file; every rule is namespaced under .wm-wrap.
import '@/assets/css/weld-mapping.css'

// Cards rendered per page. One page fills more than a tablet screen at 3 columns,
// so the next page is fetched before the user reaches the end of this one.
const PAGE_SIZE = 12

export default {
  mixins: [WeldMappingMixin],
  components: {
    'weld-map-canvas': WeldMapCanvas
  },
  filters: {
    fromNow (v) {
      return v ? moment(v).fromNow() : ''
    }
  },
  data () {
    return {
      search: '',
      loading: false,
      loadError: null,
      // Raw folder-list tree, kept in its backend shape.
      tree: null,
      // Drilldown path. Level-by-level only: no flattening, no compression.
      stack: [],
      lastOpened: [],
      // Leaf drawing tapped on this screen. Visual only until the weld map
      // canvas exists to receive it.
      selectedId: null,
      projectName: '',
      // Thumbnail data by drawing id: { drawing_file, joints }. Filled lazily
      // for the cards actually on screen, and kept across drill-downs so
      // stepping back into a level does not refetch what is already here.
      previews: {},
      // Ids already requested, so a re-render or a repeated level visit cannot
      // fire the same request twice.
      previewsRequested: [],
      // How many of `visible` are actually rendered. Grows a page at a time as
      // the sentinel scrolls into view; reset whenever the card set changes.
      limit: PAGE_SIZE,
      observer: null,
      hostUserId: this.$cookie.get(process.env.VUE_APP_HOST_USER)
        ? parseInt(this.$cookie.get(process.env.VUE_APP_HOST_USER)) : 0
    }
  },
  computed: {
    ...mapGetters(['getHostRefApi']),
    ...mapGetters('project', ['project']),
    isOnline () {
      return navigator.onLine
    },
    projectLine () {
      const name = this.projectName || (this.project && this.project.project_name) || ''
      const id = this.$route.params.project_id
      return name ? `${name} · #${id}` : `#${id}`
    },
    // Children of the node we are currently looking at, or the roots.
    currentChildren () {
      const node = this.stack.length ? this.stack[this.stack.length - 1] : this.tree
      if (!node) return []
      return (node.children || [])
    },
    // Map backend nodes to view models. Only real fields — no fabricated values.
    mapped () {
      return this.currentChildren.map(n => {
        const d = n.data || {}
        const kids = n.children || []
        return {
          id: d.id != null ? d.id : n.id,
          raw: n,
          number: n.text || '',
          description: d.description || '',
          // Tegningens tilstand som ÉN verdi (5170/B4). Kortet viste før bare
          // ENGINEERING-statusen, og den står «Ferdig» så snart sveisene er
          // kartlagt — før noe er sammenstilt, sveist eller kontrollert. Nå er
          // «Ferdig» forbeholdt tegninger der alle tre delene er ferdige.
          // Faller tilbake på engineering mot en eldre server.
          // Fargen på brikken. Den kom før fra den LAGREDE samlestatusen mens
          // ordet kom fra stadiet — så en «Ferdig»-brikke kunne bli malt grå.
          // Serveren sender nå fargeverdien avledet av samme stadium
          // (WIT-IMPL-PWA-DRAWINGSTATUS-003); eldre server faller tilbake.
          statusValue: d.drawing_stage_status !== undefined && d.drawing_stage_status !== null
            ? d.drawing_stage_status
            : (d.overall_status !== undefined && d.overall_status !== null
                ? d.overall_status
                : d.engineering_status),
          // Selve stadiet, for brikkens egen farge (VT fullført har sin egen).
          stage: d.drawing_stage || '',
          // Ordet på brikken: tegningens STADIUM (D-5170-1), oversatt her slik
          // at det følger språket brukeren har valgt i appen. Serveren sender
          // bare nøkkelen. Faller tilbake på serverens egen etikett — og til
          // slutt på engineering — mot en eldre server.
          // Ordet: appens egen oversettelse av nøkkelen først. Kjenner ikke
          // denne (bufrede) appen nøkkelen ennå, bruker vi serverens eget ord
          // for stadiet før vi faller tilbake på de lagrede statusene.
          statusLabel: this.stageLabel(d.drawing_stage)
            || d.drawing_stage_label
            || d.overall_status_label || d.engineering_status_label || '',
          // Only show a count when the API actually provided one.
          weldCount: typeof d.weld_count === 'number' ? d.weld_count : null,
          hasWeldlog: Number(d.has_weldlog) === 1,
          hasChildren: kids.length > 0,
          childCount: kids.length
        }
      })
    },
    // The node we are currently inside, if we have drilled into one.
    currentNode () {
      return this.stack.length ? this.stack[this.stack.length - 1] : null
    },
    currentNumber () {
      return this.currentNode ? (this.currentNode.text || '') : ''
    },
    // Welds belonging to that node itself, not to its children.
    currentOwnWelds () {
      const d = this.currentNode && this.currentNode.data
      return d && typeof d.weld_count === 'number' ? d.weld_count : 0
    },
    visible () {
      const q = (this.search || '').trim().toLowerCase()
      if (!q) return this.mapped
      // Drawings only — weld-number search is out of scope for this screen.
      return this.mapped.filter(d =>
        d.number.toLowerCase().includes(q) ||
        (d.description || '').toLowerCase().includes(q)
      )
    },
    // The slice of `visible` currently on screen.
    rendered () {
      return this.visible.slice(0, this.limit)
    },
    hasMore () {
      return this.visible.length > this.limit
    },
    // Every drawing with a card on screen right now — grid and Continue alike.
    // A parent card opens (and so previews) its own map, the same drawing
    // onCardClick lands on, so its own id is the right one to preview.
    visibleIds () {
      return [
        ...this.rendered.map(d => d.id),
        ...this.continueCards.map(c => c.id)
      ].filter((id, i, a) => id && a.indexOf(id) === i)
    },
    continueCards () {
      // Shown only for drawings genuinely opened before, and only while
      // browsing the top level. Scoped to this project so another project's
      // history never leaks in.
      if (this.stack.length || this.search) return []
      return (this.lastOpened || []).filter(
        l => l && l.id && String(l.projectId) === String(this.$route.params.project_id)
      )
    }
  },
  watch: {
    // Covers every way the card set changes: first load, drilling in or out,
    // and searching. Ids already fetched are skipped inside.
    visibleIds: {
      immediate: true,
      handler (ids) {
        this.loadPreviews(ids)
      }
    },
    // A new card set (drill in/out, search, reload) starts again at page one,
    // otherwise a search hit inside a deep page would render everything above it.
    visible () {
      this.limit = PAGE_SIZE
      this.$nextTick(this.observeSentinel)
    },
    hasMore () {
      this.$nextTick(this.observeSentinel)
    }
  },
  mounted () {
    this.loadRecent()
    this.loadDrawings()
  },
  beforeDestroy () {
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    // The sentinel is re-created by v-if on every page, so the observer is
    // pointed at the current element each time rather than kept on a stale one.
    observeSentinel () {
      const el = this.$refs.sentinel
      if (!el) return
      if (typeof IntersectionObserver === 'undefined') {
        // No observer support: render everything rather than trap the user on
        // page one with no way to reach the rest.
        this.limit = this.visible.length
        return
      }
      if (this.observer) this.observer.disconnect()
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) this.showMore()
      }, { rootMargin: '300px' })
      this.observer.observe(el)
    },
    showMore () {
      if (!this.hasMore) return
      this.limit += PAGE_SIZE
    },
    hasPreview (id) {
      const p = this.previews[id]
      return !!(p && p.drawing_file)
    },
    // Thumbnails are a nicety: a failure leaves the placeholder icon in place
    // and is never surfaced as an error, because the card still works.
    async loadPreviews (ids) {
      const missing = (ids || []).filter(id => id && !this.previewsRequested.includes(id))
      if (!missing.length) return
      this.previewsRequested = this.previewsRequested.concat(missing)
      try {
        const { data } = await hostAppApi.get(`${this.getHostRefApi}weld-mapping/previews`, {
          // user_id is what the document download route authenticates on, so the
          // image URLs it builds are only usable when it is sent.
          params: { ids: missing.join(','), user_id: this.hostUserId }
        })
        const previews = (data && data.previews) || {}
        // Replace the object so Vue 2 picks up the new keys.
        this.previews = { ...this.previews, ...previews }
      } catch (e) {
        // Allow a later visit to retry rather than pinning the placeholder for
        // the rest of the session.
        this.previewsRequested = this.previewsRequested.filter(id => !missing.includes(id))
      }
    },
    // "Continue" now comes from the server (weld_mapping_recent), so the list
    // follows the user across devices and openedAt is a server timestamp — the
    // old localStorage copy was device-local and used the device clock.
    async loadRecent () {
      try {
        const { data } = await hostAppApi.get(
          `${this.getHostRefApi}weld-mapping/recent/${this.$route.params.project_id}`,
          { params: { user_id: this.hostUserId } }
        )
        this.lastOpened = (data && data.recent) || []
      } catch (e) {
        // The Continue section is a convenience: on failure it is simply
        // hidden rather than blocking the drawing list.
        this.lastOpened = []
      }
    },
    // Calls the existing folder-list endpoint directly rather than the store
    // action, because that action swallows failures and commits nothing — which
    // would surface as a misleading "no drawings" empty state instead of an error.
    async loadDrawings () {
      this.loadError = null
      this.loading = true
      try {
        const projectId = this.$route.params.project_id
        const { data } = await hostAppApi.get(`${this.getHostRefApi}project-drawing/${projectId}/folder-list`)
        this.tree = data && typeof data === 'object' ? data : null
        this.stack = []
      } catch (e) {
        this.tree = null
        this.loadError = e?.response?.data?.message || this.$t('message.common.somethingWentWrong')
      } finally {
        this.loading = false
      }
    },
    nodeNumber (n) {
      return n.text || ''
    },
    /** Oversett stadie-nøkkelen fra serveren. Ukjent eller manglende nøkkel
     *  gir tom streng, og kallstedet faller tilbake på serverens etikett. */
    stageLabel (stage) {
      const key = String(stage || '')
      if (!key) return ''
      const path = 'message.weldMapping.drawingStage.' + key
      const out = this.$t(path)
      return out === path ? '' : out
    },
    statusClass (v, stage) {
      // «VT fullført» er sitt eget steg mellom sveising og ferdig, og har sin
      // egen farge; ellers OperationStatus: 0 venter, 1 pågår, 2 ferdig.
      if (stage === 'vt_done') return 'wm-tag-vt'
      return Number(v) === 2 ? 'wm-tag-done' : Number(v) === 1 ? 'wm-tag-prog' : 'wm-tag-pend'
    },
    // A parent opens its child list; a leaf opens the drawing.
    // Every card opens the drawing screen. When the card itself carries welds
    // that is the drawing to open — a parent often holds most of the welds, so
    // skipping past it to a child would strand them. Only when the parent has
    // none of its own do we descend to the first child that does.
    // Switching between the parent and its sub-drawings then happens in the
    // header's pager, not through a second card list.
    onCardClick (d) {
      if (Number(d.weldCount) > 0 || !d.hasChildren) {
        this.openDrawing(d)
        return
      }
      this.openDrawing(this.firstWeldBearingChild(d.raw) || d)
    },
    // First descendant that actually carries welds, depth-first, so the screen
    // opens on a drawing with something to show.
    firstWeldBearingChild (node) {
      for (const c of (node.children || [])) {
        const d = c.data || {}
        if (Number(d.weld_count) > 0) {
          return { id: d.id != null ? d.id : c.id, number: c.text || '', description: d.description || '' }
        }
        const deeper = this.firstWeldBearingChild(c)
        if (deeper) return deeper
      }
      return null
    },
    openDrawing (d) {
      // Fire-and-forget: recording the open must never delay navigation, and
      // a failure here only costs a Continue entry.
      hostAppApi.post(
        `${this.getHostRefApi}project-drawing/${d.id}/weld-mapping-recent`,
        { user_id: this.hostUserId }
      ).catch(() => {})
      this.selectedId = d.id
      // Opens the weld-mapping drawing screen (header bar for now; the weld map
      // fills its content area next). Never the legacy weld-log page.
      this.$router.push({
        name: 'weld-mapping-drawing',
        params: { project_id: this.$route.params.project_id, drawing_id: d.id }
      })
    },
    // Opens the parent's own weld map, the same destination a leaf card uses.
    openCurrentOwn () {
      const n = this.currentNode
      if (!n) return
      const d = n.data || {}
      this.openDrawing({
        id: d.id != null ? d.id : n.id,
        number: n.text || '',
        description: d.description || ''
      })
    },
    gotoLevel (i) {
      this.stack = this.stack.slice(0, i + 1)
      this.search = ''
    },
    resetLevel () {
      this.stack = []
      this.search = ''
    },
    goBack () {
      if (this.stack.length) {
        this.stack.pop()
        return
      }
      this.$router.push({ name: 'project_action', params: { project_id: this.$route.params.project_id } })
    }
  }
}
</script>
